import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import { Sparkles, Star, Check, X, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import useHolidayStore from '@/store/HolidayStore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Stack,
    LinearProgress,
    Alert,
    CircularProgress,
    useTheme as useMuiTheme,
    alpha,
    IconButton,
    Tooltip
} from '@mui/material';

const HolidayList: React.FC = () => {
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const theme = useMuiTheme();

    // Zustand store
    const {
        optionalHolidays,
        selectedOptionalHolidays: storeSelectedHolidays,
        loading,
        error,
        maxOptionalSelections,
        fetchOptionalHolidays,
        fetchSelectedOptionalHolidays,
        submitHolidaySelection,
        fetchHolidayConfig,
        checkYearData
    } = useHolidayStore();

    const [selectedOptionalHolidays, setSelectedOptionalHolidays] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false);
    const [currentFyYear, setCurrentFyYear] = useState<number>(() => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    });
    const [hasPrevYearData, setHasPrevYearData] = useState(false);
    const [hasNextYearData, setHasNextYearData] = useState(false);

    // Get financial year string from year number
    const getFinancialYearString = (fyYear: number) => {
        return `April ${fyYear} - March ${fyYear + 1}`;
    };

    const financialYear = getFinancialYearString(currentFyYear);

    // Check if adjacent years have data
    const checkAdjacentYears = async () => {
        const [prevHasData, nextHasData] = await Promise.all([
            checkYearData(currentFyYear - 1),
            checkYearData(currentFyYear + 1)
        ]);
        setHasPrevYearData(prevHasData);
        setHasNextYearData(nextHasData);
    };

    const handlePreviousYear = async () => {
        if (hasPrevYearData) {
            setCurrentFyYear(currentFyYear - 1);
        }
    };

    const handleNextYear = async () => {
        if (hasNextYearData) {
            setCurrentFyYear(currentFyYear + 1);
        }
    };

    // Load holidays data when year changes
    useEffect(() => {
        const loadHolidays = async () => {
            try {
                await Promise.all([
                    fetchOptionalHolidays(currentFyYear),
                    fetchSelectedOptionalHolidays(),
                    fetchHolidayConfig(currentFyYear)
                ]);
                await checkAdjacentYears();
            } catch (error) {
                console.error('Failed to load holidays:', error);
            }
        };

        loadHolidays();
        // Reset selection when year changes
        setSelectedOptionalHolidays([]);
        setHasAlreadySubmitted(false);
    }, [currentFyYear]);

    // Sync selected holidays from store to local state and check if user has already submitted
    // Filter to only show holidays for the current FY year being viewed
    useEffect(() => {
        if (storeSelectedHolidays && storeSelectedHolidays.length > 0) {
            // Filter selected holidays by current FY year
            const fyStartDate = `${currentFyYear}-04-01`;
            const fyEndDate = `${currentFyYear + 1}-03-31`;

            const yearSpecificHolidays = storeSelectedHolidays.filter((h: any) => {
                const holidayDate = h.holiday_date;
                return holidayDate >= fyStartDate && holidayDate <= fyEndDate;
            });

            if (yearSpecificHolidays.length > 0) {
                const selectedIds = yearSpecificHolidays.map((h: any) => h.id);
                setSelectedOptionalHolidays(selectedIds);
                setHasAlreadySubmitted(true);
                setSubmitSuccess(true);
            } else {
                setSelectedOptionalHolidays([]);
                setHasAlreadySubmitted(false);
                setSubmitSuccess(false);
            }
        } else {
            setSelectedOptionalHolidays([]);
            setHasAlreadySubmitted(false);
            setSubmitSuccess(false);
        }
    }, [storeSelectedHolidays, currentFyYear]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getDayName = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'long' });
    };

    const handleOptionalClick = (id: number) => {
        if (hasAlreadySubmitted) return;

        if (selectedOptionalHolidays.includes(id)) {
            setSelectedOptionalHolidays(selectedOptionalHolidays.filter(hId => hId !== id));
        } else if (selectedOptionalHolidays.length < maxOptionalSelections) {
            setSelectedOptionalHolidays([...selectedOptionalHolidays, id]);
        }
    };

    const handleRemoveSelected = (id: number) => {
        if (hasAlreadySubmitted) return;
        setSelectedOptionalHolidays(selectedOptionalHolidays.filter(hId => hId !== id));
    };

    const handleSubmitSelection = async () => {
        if (selectedOptionalHolidays.length === 0) {
            setSubmitError('Please select at least one optional holiday');
            setTimeout(() => setSubmitError(null), 3000);
            return;
        }

        if (selectedOptionalHolidays.length > maxOptionalSelections) {
            setSubmitError(`You can only select up to ${maxOptionalSelections} holidays`);
            setTimeout(() => setSubmitError(null), 3000);
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const result = await submitHolidaySelection(selectedOptionalHolidays);

            if (result.success) {
                setSubmitSuccess(true);
                setHasAlreadySubmitted(true);
                await fetchSelectedOptionalHolidays();
                setTimeout(() => setSubmitSuccess(false), 2000);
            } else {
                setSubmitError(result.error || 'Failed to save holiday selection');
                setTimeout(() => setSubmitError(null), 3000);
            }
        } catch (error: any) {
            console.error('Failed to save holiday selection:', error);
            setSubmitError(error.message || 'An error occurred while saving');
            setTimeout(() => setSubmitError(null), 3000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleViewCalendar = () => {
        navigate('/holiday-calendar-view');
    };

    return (
        <Layout>
            <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
                {/* Header */}
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                                <Box>
                                    <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                                        <Sparkles size={32} color={theme.palette.primary.main} />
                                        <Typography variant="h4" fontWeight={600} color="text.primary">
                                            Select Optional Holidays
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        Choose up to {maxOptionalSelections} optional holidays for {financialYear}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<CalendarDays size={20} />}
                                    onClick={handleViewCalendar}
                                    sx={{
                                        fontWeight: 600,
                                        px: 3,
                                        borderRadius: 2,
                                        textTransform: 'none'
                                    }}
                                >
                                    View Calendar
                                </Button>
                            </Stack>

                            {/* Year Navigation */}
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                <Tooltip title={hasPrevYearData ? `Go to FY ${currentFyYear - 1}-${currentFyYear}` : 'No data for previous year'}>
                                    <span>
                                        <IconButton
                                            onClick={handlePreviousYear}
                                            disabled={!hasPrevYearData || loading}
                                            sx={{
                                                bgcolor: hasPrevYearData ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.disabled, 0.05),
                                                '&:hover': {
                                                    bgcolor: hasPrevYearData ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.disabled, 0.05)
                                                },
                                                '&:disabled': {
                                                    opacity: 0.4
                                                }
                                            }}
                                        >
                                            <ChevronLeft size={20} color={hasPrevYearData ? theme.palette.primary.main : theme.palette.action.disabled} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Chip
                                    label={`FY ${currentFyYear}-${currentFyYear + 1}`}
                                    color="primary"
                                    sx={{ fontWeight: 600, fontSize: '0.875rem', px: 2 }}
                                />
                                <Tooltip title={hasNextYearData ? `Go to FY ${currentFyYear + 1}-${currentFyYear + 2}` : 'No data for next year'}>
                                    <span>
                                        <IconButton
                                            onClick={handleNextYear}
                                            disabled={!hasNextYearData || loading}
                                            sx={{
                                                bgcolor: hasNextYearData ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.disabled, 0.05),
                                                '&:hover': {
                                                    bgcolor: hasNextYearData ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.disabled, 0.05)
                                                },
                                                '&:disabled': {
                                                    opacity: 0.4
                                                }
                                            }}
                                        >
                                            <ChevronRight size={20} color={hasNextYearData ? theme.palette.primary.main : theme.palette.action.disabled} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Selection Progress */}
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Star size={28} color={theme.palette.primary.main} />
                                <Typography variant="h6" fontWeight={600} color="text.primary">
                                    Your Selected Holidays
                                </Typography>
                            </Stack>
                            <Chip
                                label={`${selectedOptionalHolidays.length} / ${maxOptionalSelections}`}
                                color={selectedOptionalHolidays.length === maxOptionalSelections ? 'success' : 'default'}
                                sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                            />
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={(selectedOptionalHolidays.length / maxOptionalSelections) * 100}
                            sx={{
                                height: 8,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 1,
                                    bgcolor: selectedOptionalHolidays.length === maxOptionalSelections
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main
                                }
                            }}
                        />

                        {/* Selected Holidays List */}
                        {selectedOptionalHolidays.length > 0 && (
                            <Box
                                mt={3}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, 1fr)',
                                        md: 'repeat(4, 1fr)'
                                    },
                                    gap: 2
                                }}
                            >
                                {selectedOptionalHolidays.map((id, index) => {
                                    const holiday = optionalHolidays?.find((h: any) => h.id === id);
                                    if (!holiday) return null;
                                    return (
                                        <Card
                                            key={id}
                                            sx={{
                                                borderRadius: 2,
                                                border: 2,
                                                borderColor: 'success.main',
                                                bgcolor: alpha(theme.palette.success.main, 0.05),
                                                position: 'relative',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    boxShadow: 3,
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 2 }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                    <Chip
                                                        label={index + 1}
                                                        size="small"
                                                        color="success"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                    {!hasAlreadySubmitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveSelected(id)}
                                                            sx={{ color: 'error.main' }}
                                                        >
                                                            <X size={18} />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                                <Typography variant="subtitle1" fontWeight={600} color="text.primary" mt={1}>
                                                    {holiday.holiday_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(holiday.holiday_date)} • {getDayName(holiday.holiday_date)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Submit Button */}
                        {selectedOptionalHolidays.length > 0 && (
                            <Stack spacing={2} alignItems="center" mt={3}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Check size={20} />}
                                    onClick={handleSubmitSelection}
                                    disabled={isSubmitting || hasAlreadySubmitted || selectedOptionalHolidays.length !== maxOptionalSelections}
                                    sx={{
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        bgcolor: (isSubmitting || hasAlreadySubmitted || selectedOptionalHolidays.length !== maxOptionalSelections)
                                            ? 'grey.500'
                                            : 'success.main',
                                        '&:hover': {
                                            bgcolor: (isSubmitting || hasAlreadySubmitted || selectedOptionalHolidays.length !== maxOptionalSelections)
                                                ? 'grey.500'
                                                : 'success.dark'
                                        }
                                    }}
                                >
                                    {isSubmitting ? 'Saving...' : hasAlreadySubmitted ? 'Submitted' : 'Submit Selection'}
                                </Button>

                                {submitError && (
                                    <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ width: '100%' }}>
                                        {submitError}
                                    </Alert>
                                )}

                                {selectedOptionalHolidays.length !== maxOptionalSelections && !hasAlreadySubmitted && (
                                    <Alert severity="info" sx={{ width: '100%' }}>
                                        Please select exactly {maxOptionalSelections} holidays to submit your selection.
                                    </Alert>
                                )}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                {/* Optional Holidays List */}
                <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
                            <Sparkles size={28} color={theme.palette.warning.main} />
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                                Available Optional Holidays
                            </Typography>
                            <Chip
                                label={optionalHolidays?.length || 0}
                                size="small"
                                color="warning"
                                sx={{ fontWeight: 600 }}
                            />
                        </Stack>

                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                                <CircularProgress />
                                <Typography color="text.secondary">Loading holidays...</Typography>
                            </Box>
                        ) : (!optionalHolidays || optionalHolidays.length === 0) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                                <Sparkles size={64} color={theme.palette.action.disabled} />
                                <Typography variant="h6" fontWeight={600} color="text.primary">No Optional Holidays Available</Typography>
                                <Typography color="text.secondary">There are no optional holidays for this year.</Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, 1fr)',
                                        md: 'repeat(3, 1fr)',
                                        lg: 'repeat(4, 1fr)'
                                    },
                                    gap: 2
                                }}
                            >
                                {optionalHolidays.map((holiday: any) => {
                                    const isSelected = selectedOptionalHolidays.includes(holiday.id);
                                    const isDisabled = hasAlreadySubmitted || (!isSelected && selectedOptionalHolidays.length >= maxOptionalSelections);

                                    return (
                                        <Card
                                            key={holiday.id}
                                            onClick={() => !isDisabled && !hasAlreadySubmitted && handleOptionalClick(holiday.id)}
                                            sx={{
                                                borderRadius: 2,
                                                border: 2,
                                                borderColor: isSelected ? 'success.main' : 'divider',
                                                bgcolor: isSelected
                                                    ? alpha(theme.palette.success.main, 0.05)
                                                    : 'background.paper',
                                                cursor: isDisabled || hasAlreadySubmitted ? 'not-allowed' : 'pointer',
                                                opacity: isDisabled ? 0.5 : 1,
                                                transition: 'all 0.2s ease',
                                                position: 'relative',
                                                '&:hover': !isDisabled && !hasAlreadySubmitted ? {
                                                    boxShadow: 3,
                                                    transform: 'translateY(-4px)',
                                                    borderColor: isSelected ? 'success.dark' : 'primary.main'
                                                } : {}
                                            }}
                                        >
                                            <CardContent sx={{ p: 2.5 }}>
                                                <Stack spacing={1}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                        <Box sx={{
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: 2,
                                                            bgcolor: isSelected
                                                                ? alpha(theme.palette.success.main, 0.15)
                                                                : alpha(theme.palette.warning.main, 0.15),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            {isSelected ? (
                                                                <Star size={24} color={theme.palette.success.main} fill={theme.palette.success.main} />
                                                            ) : (
                                                                <Sparkles size={24} color={theme.palette.warning.main} />
                                                            )}
                                                        </Box>
                                                        {isSelected && (
                                                            <Chip
                                                                label="Selected"
                                                                size="small"
                                                                color="success"
                                                                sx={{ fontWeight: 600 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    <Typography variant="h6" fontWeight={600} color="text.primary">
                                                        {holiday.holiday_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(holiday.holiday_date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {getDayName(holiday.holiday_date)}
                                                    </Typography>
                                                    {!isSelected && isDisabled && (
                                                        <Chip
                                                            label="Limit Reached"
                                                            size="small"
                                                            color="default"
                                                            sx={{ fontWeight: 500, mt: 1 }}
                                                        />
                                                    )}
                                                    {!isSelected && !isDisabled && !hasAlreadySubmitted && (
                                                        <Chip
                                                            label="Click to Select"
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ fontWeight: 500, mt: 1 }}
                                                        />
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Layout>
    );
};

export default HolidayList;
